<template>
    <div :style="{ height: height + 'px', width: '100%' }" ref="mapContainer"></div>
</template>

<script setup>
import { onMounted, ref, watch, onBeforeUnmount } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const props = defineProps({
    coordinates: {
        type: Object,
        required: true,
        validator: (val) => 'lat' in val && 'lng' in val,
    },
    color: {
        type: String,
        default: '#3388ff',
    },
    height: {
        type: Number,
        default: 300,
    },
    pan: {
        type: Array,
        default: [0, 0]
    }
});

const mapContainer = ref(null);
let map = null;
let marker = null;

onMounted(() => {
    if (mapContainer.value) {
        map = L.map(mapContainer.value, {
            center: [props.coordinates.lat, props.coordinates.lng],
            zoom: 16,
            zoomControl: false,
            attributionControl: false,
        });

        map.dragging.disable();
        map.touchZoom.disable();
        map.doubleClickZoom.disable();
        map.scrollWheelZoom.disable();
        map.boxZoom.disable();
        map.keyboard.disable();
        map.panBy(props.pan);

        const layer = document.querySelector('html').classList.contains('dark') ? 'dark' : 'light';

        L.tileLayer(`https://{s}.basemaps.cartocdn.com/${layer}_all/{z}/{x}/{y}{r}.png`, {
            maxZoom: 19,
        }).addTo(map);

        const icon = L.divIcon({
            className: 'custom-icon',
            html: '<i class="bi bi-geo-alt-fill text-' + props.color + '-500" style="font-size: 2rem;"></i>',
            iconSize: [30, 30],
            iconAnchor: [15, 30]
        });

        marker = L.marker([props.coordinates.lat, props.coordinates.lng], {icon}).addTo(map);

        const waitForVisibility = () => {
            const el = mapContainer.value;
            if (el && el.offsetWidth > 0 && el.offsetHeight > 0) {
                map.invalidateSize();
                window.dispatchEvent(new Event('resize'));
                map.panBy(props.pan);
            } else {
                requestAnimationFrame(waitForVisibility);
            }
        };
        waitForVisibility();
    }
});

watch(() => props.coordinates, (newCoords) => {
    if (map && newCoords) {
        map.setView([newCoords.lat, newCoords.lng]);
        if (marker) {
            marker.setLatLng([newCoords.lat, newCoords.lng]);
        }
    }
});

onBeforeUnmount(() => {
    if (map) {
        map.remove();
        map = null;
    }
});
</script>

<style scoped>


.leaflet-map {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
}

.leaflet-control {
    display: none;
}

.leaflet-map:hover .leaflet-control {
    display: block;
}

.leaflet-map:hover .leaflet-tile-container {
    opacity: 0.8;
}

.leaflet-map .leaflet-control-attribution {
    opacity: 0;
    transition: opacity 0.3s;
}

.leaflet-map:hover .leaflet-control-attribution {
    opacity: 1;
}
</style>
